// «Coste servicios» (Administración, 4-nov-2026): what each service of VegaValor costs per
// day, week, month and year (AI by area, Tavily, fixed subscriptions), the user counter and
// the spend per user. Data: GET /api/admin/service-costs (services/service_costs.py).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { Loader2, RefreshCw, Info, Maximize2, X, Download, Users, Plus, Trash2, Save, AlertTriangle, BarChart3, CalendarDays, Server } from "lucide-react";
import { toast } from "sonner";
import { adminServiceCosts, adminServiceCostsSettings, shareUpload } from "@/lib/api";
import { downloadSvgJpg, getSvgJpgBlob } from "@/lib/chartExport";
import ShareMenu from "@/components/ShareMenu";
import HoverTip from "@/components/HoverTip";

const GROUP_COLORS = {
    "Inteligencia artificial": "var(--chart-blue)",
    "Búsqueda web": "#C99700",
    "Datos de mercado": "#1D7044",
    "Infraestructura": "#8E5CC2",
};
const EXTRA_COLORS = ["#D2691E", "#2E8B8B", "#B03A7A", "#7A7A7A"];
const colorOf = (g, i) => GROUP_COLORS[g] || EXTRA_COLORS[i % EXTRA_COLORS.length];

const usd = (n, digits) => {
    const v = Number(n) || 0;
    const d = digits != null ? digits : (Math.abs(v) >= 100 ? 0 : Math.abs(v) >= 1 ? 2 : v === 0 ? 2 : 3);
    return `${v.toLocaleString("es-ES", { minimumFractionDigits: d, maximumFractionDigits: d })} $`;
};
const int = (n) => (n == null ? "—" : Number(n).toLocaleString("es-ES"));
const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const monthLabel = (m) => { const [y, mo] = m.split("-").map(Number); return `${MONTHS[mo - 1]}'${String(y).slice(2)}`; };
const dayLabel = (d) => { const [, mo, da] = d.split("-").map(Number); return `${da} ${MONTHS[mo - 1]}`; };

const COLS = [
    ["today", "Hoy"], ["last_7", "7 días"], ["month", "Mes en curso"], ["month_forecast", "Previsión del mes"],
    ["prev_month", "Mes anterior"], ["last_365", "12 meses"],
];

function Tip({ text }) {
    return (
        <HoverTip text={text}>
            <button className="text-[#9A9A9A] hover:text-[#052049] shrink-0" aria-label="Más información"><Info size={13} /></button>
        </HoverTip>
    );
}

function Stat({ label, value, sub, tip, testid }) {
    return (
        <div className="border border-black/15 bg-white p-3 h-full" data-testid={testid}>
            <div className="overline text-[#4A4A4A] flex items-center gap-1.5">{label}{tip && <Tip text={tip} />}</div>
            <div className="font-mono text-2xl mt-1 break-words">{value}</div>
            {sub && <div className="text-[11px] text-[#4A4A4A] mt-1 leading-snug">{sub}</div>}
        </div>
    );
}

function CostTooltip({ active, payload, label, groups, users }) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload || {};
    return (
        <div className="bg-[#111111] text-white text-[11px] px-2.5 py-2 shadow-lg min-w-[10rem]">
            <div className="font-semibold mb-1">{row.label || label}{row.partial ? " (en curso)" : ""}</div>
            {groups.filter((g) => row[g]).map((g, i) => (
                <div key={g} className="flex justify-between gap-3">
                    <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2" style={{ background: colorOf(g, i) }} />{g}</span>
                    <span className="font-mono">{usd(row[g])}</span>
                </div>
            ))}
            <div className="flex justify-between gap-3 border-t border-white/20 mt-1 pt-1 font-semibold"><span>Total</span><span className="font-mono">{usd(row.total)}</span></div>
            {users && row.active_users != null && (
                <div className="flex justify-between gap-3 mt-1" style={{ color: "#F2A541" }}><span>Usuarios activos</span><span className="font-mono">{int(row.active_users)}</span></div>
            )}
            {users && row.usd_per_active != null && (
                <div className="flex justify-between gap-3"><span>Por usuario activo</span><span className="font-mono">{usd(row.usd_per_active)}</span></div>
            )}
        </div>
    );
}

function CostChart({ data, groups, height, users, xKey = "label", small }) {
    const hasUsers = users && data.some((d) => d.active_users != null);
    return (
        <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={data} margin={{ top: 6, right: hasUsers ? 4 : 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" vertical={false} />
                <XAxis dataKey={xKey} tick={{ fontSize: small ? 9 : 10 }} interval="preserveStartEnd" minTickGap={12} />
                <YAxis yAxisId="usd" tick={{ fontSize: 10 }} width={44} tickFormatter={(v) => `${v >= 100 ? Math.round(v) : Number(v).toFixed(v >= 10 ? 0 : 1)}$`} />
                {hasUsers && <YAxis yAxisId="u" orientation="right" tick={{ fontSize: 10, fill: "#F2A541" }} width={30} allowDecimals={false} />}
                <RTooltip content={<CostTooltip groups={groups} users={users} />} cursor={{ fill: "rgba(0,0,0,0.05)" }} />
                {groups.map((g, i) => <Bar key={g} yAxisId="usd" dataKey={g} stackId="c" fill={colorOf(g, i)} isAnimationActive={false} />)}
                {hasUsers && <Line yAxisId="u" type="monotone" dataKey="active_users" stroke="#F2A541" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} connectNulls />}
            </ComposedChart>
        </ResponsiveContainer>
    );
}

function Legend({ groups, users }) {
    return (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-[#4A4A4A] mt-2">
            {groups.map((g, i) => <span key={g} className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5" style={{ background: colorOf(g, i) }} />{g}</span>)}
            {users && <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: "#F2A541" }} />Usuarios activos (eje derecho)</span>}
        </div>
    );
}

function ChartModal({ title, icon: Icon, children, note, filename, onClose }) {
    const ref = useRef(null);
    const [busy, setBusy] = useState(false);
    const dl = async () => {
        setBusy(true);
        try { await downloadSvgJpg(ref.current, filename); } catch { toast.error("No se pudo exportar el gráfico"); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
            <div className="bg-white border-2 border-[#052049] w-full max-w-5xl p-4 sm:p-5 flex flex-col max-h-[95vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 mb-3 shrink-0">
                    <h2 className="font-serif text-lg sm:text-2xl text-[#052049] flex items-center gap-2 min-w-0">
                        <Icon size={22} className="shrink-0" /> <span className="truncate">{title}</span>
                    </h2>
                    <div className="flex items-center gap-3 shrink-0">
                        <ShareMenu size="md" title={`${title} · VegaValor`} testidPrefix={`${filename}-share`}
                            createShare={async () => shareUpload(await getSvgJpgBlob(ref.current), "jpg", title)} />
                        <button onClick={dl} disabled={busy} className="text-[#7A7A7A] hover:text-[#052049] inline-flex items-center gap-1 text-xs uppercase tracking-wide disabled:opacity-50" title="Descargar en JPG">
                            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} JPG
                        </button>
                        <button onClick={onClose} className="text-[#7A7A7A] hover:text-[#052049]" aria-label="Cerrar"><X size={20} /></button>
                    </div>
                </div>
                <div ref={ref} className="w-full h-[50vh] min-h-[240px] max-h-[460px] shrink-0 bg-white">{children}</div>
                {note && <p className="text-[11px] text-[#7A7A7A] mt-3 leading-relaxed shrink-0">{note}</p>}
            </div>
        </div>
    );
}

function ChartCard({ title, icon: Icon, tip, data, groups, users, filename, note, testid }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-black/15 bg-white p-3 flex flex-col h-full" data-testid={testid}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="overline text-[#4A4A4A] flex items-center gap-1.5">
                    <Icon size={13} className="text-[#052049]" /> {title} <Tip text={tip} />
                </div>
                <button onClick={() => setOpen(true)} className="text-[#9A9A9A] hover:text-[#052049] shrink-0" aria-label="Ampliar" title="Ampliar"><Maximize2 size={14} /></button>
            </div>
            <div className="flex-1 min-h-[220px]">
                {data.length ? <CostChart data={data} groups={groups} users={users} height="100%" small /> :
                    <div className="h-full flex items-center justify-center text-xs text-[#7A7A7A] italic">Sin datos todavía.</div>}
            </div>
            <Legend groups={groups} users={users} />
            {open && (
                <ChartModal title={title} icon={Icon} filename={filename} note={note} onClose={() => setOpen(false)}>
                    <CostChart data={data} groups={groups} users={users} height="100%" />
                </ChartModal>
            )}
        </div>
    );
}

function ServicesTable({ rows, totals, groups }) {
    const byGroup = groups.map((g) => [g, rows.filter((r) => r.group === g)]).filter(([, rs]) => rs.length);
    return (
        <div className="border border-black/15 bg-white" data-testid="sc-table">
            <div className="overflow-x-auto">
                <table className="w-full text-[12px] min-w-[44rem]">
                    <thead>
                        <tr className="border-b border-black text-left">
                            <th className="py-2 px-2 font-semibold">Servicio</th>
                            {COLS.map(([k, l]) => <th key={k} className="py-2 px-2 font-semibold text-right whitespace-nowrap">{l}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {byGroup.map(([g, rs], gi) => {
                            const sub = Object.fromEntries(COLS.map(([k]) => [k, rs.reduce((s, r) => s + (r.usd?.[k] || 0), 0)]));
                            return (
                                <React.Fragment key={g}>
                                    <tr className="bg-black/[0.04] border-t border-black/15">
                                        <td className="py-1.5 px-2 font-semibold"><span className="inline-block w-2.5 h-2.5 mr-1.5 align-middle" style={{ background: colorOf(g, gi) }} />{g}</td>
                                        {COLS.map(([k]) => <td key={k} className="py-1.5 px-2 text-right font-mono whitespace-nowrap font-semibold">{usd(sub[k])}</td>)}
                                    </tr>
                                    {rs.map((r) => (
                                        <tr key={r.key} className="border-t border-black/5" data-testid={`sc-row-${r.key}`}>
                                            <td className="py-1.5 px-2 pl-6">
                                                <span className="inline-flex items-center gap-1.5 flex-wrap">
                                                    {r.name}
                                                    {r.shared && <span className="text-[9px] uppercase tracking-wider border border-black/20 px-1 text-[#7A7A7A]">compartido</span>}
                                                    {r.test && <span className="text-[9px] uppercase tracking-wider border border-black/20 px-1 text-[#7A7A7A]">pruebas</span>}
                                                    {r.kind === "fixed" && !r.configured && <span className="text-[9px] uppercase tracking-wider border border-[#B8860B] px-1 text-[#7a5a10]">sin importe</span>}
                                                    {(r.note || r.models_30 || r.usage_30) && <Tip text={rowTip(r)} />}
                                                </span>
                                            </td>
                                            {COLS.map(([k]) => <td key={k} className={`py-1.5 px-2 text-right font-mono whitespace-nowrap ${!r.usd?.[k] ? "text-[#B0B0B0]" : ""}`}>{usd(r.usd?.[k])}</td>)}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                        <tr className="border-t-2 border-black">
                            <td className="py-2 px-2 font-semibold">Total</td>
                            {COLS.map(([k]) => <td key={k} className="py-2 px-2 text-right font-mono whitespace-nowrap font-semibold" data-testid={`sc-total-${k}`}>{usd(totals?.[k])}</td>)}
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function rowTip(r) {
    const lines = [];
    if (r.note) lines.push(r.note);
    if (r.kind === "fixed") {
        lines.push(r.monthly_now != null ? `Importe mensual vigente: ${usd(r.monthly_now)}.` : "Sin importe mensual: apúntalo abajo, en «Servicios fijos».");
        if (r.invoice_month != null) lines.push(`Factura apuntada de este mes: ${usd(r.invoice_month)}.`);
    }
    const u = r.usage_30;
    if (u) {
        if (r.kind === "search") lines.push(`30 días: ${int(u.calls - (u.cache_hits || 0))} búsquedas (${int(u.credits)} créditos) y ${int(u.cache_hits)} servidas desde el banco de búsquedas.`);
        else lines.push(`30 días: ${int(u.calls)} llamadas · ${int(u.input_tokens)} tokens de entrada (${int(u.cache_read_tokens)} leídos de la caché) · ${int(u.output_tokens)} de salida.`);
    }
    if (r.models_30) lines.push("Por modelo (30 días):\n" + Object.entries(r.models_30).map(([m, v]) => `  ${m}: ${usd(v)}`).join("\n"));
    if (r.shared) lines.push("Trabajo compartido: se calcula una vez por empresa para todos los usuarios.");
    return lines.join("\n\n");
}

function UsersTable({ per, users }) {
    const [all, setAll] = useState(false);
    const list = per?.users || [];
    const shown = all ? list : list.slice(0, 15);
    return (
        <div className="border border-black/15 bg-white p-3" data-testid="sc-users">
            <div className="overline text-[#4A4A4A] flex items-center gap-1.5 mb-2">
                <Users size={13} className="text-[#052049]" /> Gasto por usuario
                <Tip text={"Lo que la IA y las búsquedas web gastan en las acciones de cada usuario (sus tesis, KPI, chat…).\n\nNo incluye el trabajo compartido por todos (la proyección «Nuestro» y las cifras de empresas leídas con IA, que se calculan una vez por empresa), ni los servicios fijos, ni las pruebas del administrador: esa parte va abajo, repartida entre los usuarios activos.\n\nSe anota desde el 4-nov-2026."} />
            </div>
            {!list.length ? <div className="text-xs text-[#7A7A7A] italic py-3">Aún sin datos por usuario: se anotan desde hoy (4-nov-2026).</div> : (
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px] min-w-[36rem]">
                        <thead>
                            <tr className="border-b border-black text-left">
                                <th className="py-1.5 px-2 font-semibold">Usuario</th>
                                <th className="py-1.5 px-2 font-semibold text-right">Hoy</th>
                                <th className="py-1.5 px-2 font-semibold text-right">7 días</th>
                                <th className="py-1.5 px-2 font-semibold text-right">Mes</th>
                                <th className="py-1.5 px-2 font-semibold text-right">30 días</th>
                                <th className="py-1.5 px-2 font-semibold">En qué (30 días)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((u) => (
                                <tr key={u.user_id} className="border-t border-black/5">
                                    <td className="py-1.5 px-2 max-w-[14rem] truncate" title={u.email || u.user_id}>{u.name || u.email || u.user_id}</td>
                                    {["today", "last_7", "month", "last_30"].map((k) => <td key={k} className="py-1.5 px-2 text-right font-mono whitespace-nowrap">{usd(u.usd?.[k])}</td>)}
                                    <td className="py-1.5 px-2 text-[11px] text-[#4A4A4A]">
                                        {Object.entries(u.areas_30 || {}).slice(0, 3).map(([a, v]) => `${a} ${usd(v)}`).join(" · ") || "—"}
                                        {u.triggered_shared_30 > 0 && <span className="text-[#9A9A9A]"> · inició {usd(u.triggered_shared_30)} de trabajo compartido</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {list.length > 15 && <button onClick={() => setAll((v) => !v)} className="text-[11px] text-[#052049] hover:underline mt-2">{all ? "Ver menos" : `Ver los ${list.length}`}</button>}
            <div className="mt-3 border-t border-black/10 pt-2 text-[11px] text-[#4A4A4A] leading-relaxed">
                Últimos 30 días: gasto directo de los usuarios {usd(per?.direct_30)} · compartido y fijos {usd(per?.shared_30)}
                {per?.active_30 ? <> entre {int(per.active_30)} usuarios activos = <span className="font-semibold">{usd(per.shared_per_active_30)} por usuario</span></> : " (sin usuarios activos registrados todavía)"}
                {per?.tests_30 > 0 && <> · pruebas del administrador {usd(per.tests_30)} (fuera del reparto)</>}.
            </div>
        </div>
    );
}

const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

function FixedServicesEditor({ settings, onSaved }) {
    const [svcs, setSvcs] = useState(settings.services);
    const [credit, setCredit] = useState(String(settings.tavily_usd_per_credit ?? ""));
    const [draft, setDraft] = useState({}); // key → {amount, from, invMonth, invAmount}
    const [saving, setSaving] = useState(false);
    const [newName, setNewName] = useState("");
    useEffect(() => { setSvcs(settings.services); setCredit(String(settings.tavily_usd_per_credit ?? "")); }, [settings]);

    const persist = async (services, okMsg) => {
        setSaving(true);
        try {
            const res = await adminServiceCostsSettings({ services, tavily_usd_per_credit: Number(String(credit).replace(",", ".")) });
            setSvcs(res.services); setDraft({}); toast.success(okMsg); onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
        finally { setSaving(false); }
    };
    const d = (k) => draft[k] || {};
    const setD = (k, patch) => setDraft((x) => ({ ...x, [k]: { ...x[k], ...patch } }));
    const addPeriod = (s) => {
        const amt = Number(String(d(s.key).amount ?? "").replace(",", "."));
        if (!(amt >= 0) || d(s.key).amount === "" || d(s.key).amount == null) return toast.error("Escribe el importe mensual en $");
        const from = d(s.key).from || today();
        const periods = [...(s.periods || []).filter((p) => p.from !== from), { from, usd_month: amt }];
        persist(svcs.map((x) => (x.key === s.key ? { ...x, periods } : x)), `${s.name}: ${usd(amt)} al mes desde el ${from}`);
    };
    const addInvoice = (s) => {
        const m = d(s.key).invMonth || thisMonth();
        const amt = Number(String(d(s.key).invAmount ?? "").replace(",", "."));
        if (!(amt >= 0) || d(s.key).invAmount === "" || d(s.key).invAmount == null) return toast.error("Escribe el importe de la factura en $");
        persist(svcs.map((x) => (x.key === s.key ? { ...x, invoices: { ...(x.invoices || {}), [m]: amt } } : x)), `${s.name}: factura de ${m} apuntada`);
    };
    const delPeriod = (s, from) => persist(svcs.map((x) => (x.key === s.key ? { ...x, periods: x.periods.filter((p) => p.from !== from) } : x)), "Importe borrado");
    const delInvoice = (s, m) => persist(svcs.map((x) => { if (x.key !== s.key) return x; const inv = { ...(x.invoices || {}) }; delete inv[m]; return { ...x, invoices: inv }; }), "Factura borrada");
    const removeSvc = (s) => { if (window.confirm(`¿Quitar ${s.name} y su histórico de importes?`)) persist(svcs.filter((x) => x.key !== s.key), `${s.name} quitado`); };
    const addSvc = () => {
        const name = newName.trim();
        if (!name) return;
        persist([...svcs, { name, group: "Otros", periods: [], invoices: {} }], `${name} añadido`).then(() => setNewName(""));
    };
    const DEFAULT_KEYS = ["railway", "cloudflare", "mongodb", "resend", "marketstack", "fmp"];

    return (
        <div className="border border-black/15 bg-white p-3" data-testid="sc-fixed">
            <div className="overline text-[#4A4A4A] flex items-center gap-1.5 mb-1">
                <Server size={13} className="text-[#052049]" /> Servicios fijos y precios
                <Tip text={"Lo que pagas cada mes por suscripción. Apunta el importe mensual y desde cuándo: si cambia el precio, añade otro importe con la nueva fecha y se conserva el histórico.\n\nSi el servicio se cobra por uso (Railway), apunta además la factura real de cada mes: sustituye a la estimación de ese mes.\n\nTodo en dólares. Staging y producción comparten estos ajustes."} />
            </div>
            <p className="text-[11px] text-[#4A4A4A] mb-3">Importe en dólares al mes; se reparte entre los días del mes. La factura real de un mes, si la apuntas, manda sobre la estimación.</p>
            <div className="flex flex-col divide-y divide-black/10">
                {svcs.map((s) => {
                    const cur = [...(s.periods || [])].reverse().find((p) => p.from <= today());
                    return (
                        <div key={s.key} className="py-2.5" data-testid={`sc-fixed-${s.key}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm">{s.name}</span>
                                <span className="text-[10px] text-[#7A7A7A]">{s.group}</span>
                                <span className="text-[11px] font-mono ml-auto">{cur ? `${usd(cur.usd_month)} / mes` : <span className="text-[#7a5a10]">sin importe</span>}</span>
                                {!DEFAULT_KEYS.includes(s.key) && <button onClick={() => removeSvc(s)} disabled={saving} className="text-[#9A9A9A] hover:text-[#B32A22]" aria-label="Quitar"><Trash2 size={13} /></button>}
                            </div>
                            {s.note && <div className="text-[11px] text-[#7A7A7A] mt-0.5">{s.note}</div>}
                            <div className="flex flex-wrap items-end gap-2 mt-2">
                                <label className="flex flex-col gap-0.5 text-[10px] text-[#4A4A4A]">Importe mensual ($)
                                    <input inputMode="decimal" value={d(s.key).amount ?? ""} onChange={(e) => setD(s.key, { amount: e.target.value })} placeholder={cur ? String(cur.usd_month) : "0"} className="input-paper font-mono w-24 !py-1 text-xs" />
                                </label>
                                <label className="flex flex-col gap-0.5 text-[10px] text-[#4A4A4A]">Desde
                                    <input type="date" value={d(s.key).from || today()} onChange={(e) => setD(s.key, { from: e.target.value })} className="input-paper w-36 !py-1 text-xs" />
                                </label>
                                <button onClick={() => addPeriod(s)} disabled={saving} className="btn-ghost !px-2.5 !py-1 text-xs flex items-center gap-1 disabled:opacity-50"><Save size={12} /> Guardar importe</button>
                                <span className="w-px h-6 bg-black/10 hidden sm:block" />
                                <label className="flex flex-col gap-0.5 text-[10px] text-[#4A4A4A]">Factura del mes
                                    <input type="month" value={d(s.key).invMonth || thisMonth()} onChange={(e) => setD(s.key, { invMonth: e.target.value })} className="input-paper w-36 !py-1 text-xs" />
                                </label>
                                <label className="flex flex-col gap-0.5 text-[10px] text-[#4A4A4A]">Importe ($)
                                    <input inputMode="decimal" value={d(s.key).invAmount ?? ""} onChange={(e) => setD(s.key, { invAmount: e.target.value })} className="input-paper font-mono w-24 !py-1 text-xs" />
                                </label>
                                <button onClick={() => addInvoice(s)} disabled={saving} className="btn-ghost !px-2.5 !py-1 text-xs flex items-center gap-1 disabled:opacity-50"><Plus size={12} /> Apuntar factura</button>
                            </div>
                            {((s.periods || []).length > 0 || Object.keys(s.invoices || {}).length > 0) && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {(s.periods || []).map((p) => (
                                        <span key={p.from} className="text-[10px] border border-black/15 px-1.5 py-0.5 inline-flex items-center gap-1">
                                            desde {p.from}: {usd(p.usd_month)}/mes
                                            <button onClick={() => delPeriod(s, p.from)} disabled={saving} className="text-[#9A9A9A] hover:text-[#B32A22]" aria-label="Borrar"><X size={10} /></button>
                                        </span>
                                    ))}
                                    {Object.entries(s.invoices || {}).sort().map(([m, v]) => (
                                        <span key={m} className="text-[10px] border border-[#1D7044]/40 text-[#1D7044] px-1.5 py-0.5 inline-flex items-center gap-1">
                                            factura {m}: {usd(v)}
                                            <button onClick={() => delInvoice(s, m)} disabled={saving} className="text-[#9A9A9A] hover:text-[#B32A22]" aria-label="Borrar"><X size={10} /></button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="flex flex-wrap items-end gap-2 mt-3 border-t border-black/10 pt-3">
                <label className="flex flex-col gap-0.5 text-[10px] text-[#4A4A4A]">Otro servicio
                    <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="p. ej. Dominio, Google Workspace…" className="input-paper w-56 max-w-full !py-1 text-xs" />
                </label>
                <button onClick={addSvc} disabled={saving || !newName.trim()} className="btn-ghost !px-2.5 !py-1 text-xs flex items-center gap-1 disabled:opacity-50"><Plus size={12} /> Añadir</button>
                <span className="flex-1" />
                <label className="flex flex-col gap-0.5 text-[10px] text-[#4A4A4A]">Tavily: $ por crédito
                    <input inputMode="decimal" value={credit} onChange={(e) => setCredit(e.target.value)} className="input-paper font-mono w-24 !py-1 text-xs" />
                </label>
                <button onClick={() => persist(svcs, "Precio de Tavily guardado")} disabled={saving} className="btn-ghost !px-2.5 !py-1 text-xs flex items-center gap-1 disabled:opacity-50">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Guardar
                </button>
            </div>
            <p className="text-[10px] text-[#7A7A7A] mt-1">Tavily cobra 0,008 $ por crédito en pago por uso (una búsqueda básica = 1 crédito). Con un plan mensual, pon la cuota ÷ los créditos del plan.</p>
        </div>
    );
}

export default function ServiceCostsAdmin({ marketstackPanel }) {
    const [data, setData] = useState(undefined);
    const [days, setDays] = useState(90);
    const [loading, setLoading] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        try { setData(await adminServiceCosts(days)); } catch { setData(null); } finally { setLoading(false); }
    }, [days]);
    useEffect(() => { load(); }, [load]);

    const groups = data?.groups || [];
    const monthly = useMemo(() => (data?.monthly || []).map((m) => ({ ...m, ...m.groups, label: monthLabel(m.month) })), [data]);
    const daily = useMemo(() => (data?.daily || []).map((d) => ({ ...d, ...d.groups, label: dayLabel(d.day) })), [data]);

    if (data === undefined) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
    if (!data) return <div className="border border-black/15 bg-white p-8 text-center text-sm text-[#4A4A4A]">No se pudo cargar el coste de los servicios.</div>;
    const t = data.totals || {};
    const u = data.users || {};
    const per = data.per_user || {};
    const unconfigured = (data.rows || []).filter((r) => r.kind === "fixed" && !r.configured);
    const unpriced = Object.entries(data.unpriced || {});

    return (
        <div data-testid="admin-service-costs">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="font-serif text-xl leading-none">Coste servicios</div>
                    <div className="overline text-[#4A4A4A] mt-1">Lo que cuesta VegaValor por servicio, día, semana, mes y año</div>
                </div>
                <button onClick={load} disabled={loading} className="btn-ghost !px-3 !py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-50" data-testid="sc-reload">
                    {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Actualizar
                </button>
            </div>

            {unconfigured.length > 0 && (
                <div className="border border-[#B8860B] bg-[#FCF6E8] p-3 mb-4 flex items-start gap-2" data-testid="sc-unconfigured">
                    <AlertTriangle size={14} className="text-[#B8860B] shrink-0 mt-0.5" />
                    <p className="text-[11px] text-[#7a5a10] leading-snug">
                        Faltan los importes de {unconfigured.map((r) => r.name).join(", ")}: apúntalos en «Servicios fijos y precios» (al final) para que el total incluya lo que pagas por ellos.
                    </p>
                </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                <Stat testid="sc-today" label="Hoy" value={usd(t.today)} sub={`Ayer ${usd(t.yesterday)}`} tip="Día UTC. Incluye la parte del día de los servicios fijos (su importe mensual ÷ días del mes)." />
                <Stat testid="sc-week" label="Últimos 7 días" value={usd(t.last_7)} sub={`Media ${usd((t.last_7 || 0) / 7)} al día`} />
                <Stat testid="sc-month" label="Este mes" value={usd(t.month)} sub={`Previsión ${usd(t.month_forecast)} · mes anterior ${usd(t.prev_month)}`} tip="Previsión: lo variable (IA y búsquedas) al ritmo diario de este mes y los fijos del mes entero." />
                <Stat testid="sc-year" label="Últimos 12 meses" value={usd(t.last_365)} sub={`Media ${usd((t.last_365 || 0) / 12)} al mes`} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <Stat testid="sc-users-total" label="Usuarios registrados" value={int(u.registered)} sub={`${int(u.new_30)} nuevos en 30 días`} />
                <Stat testid="sc-users-active" label="Usuarios activos" value={int(u.active_30)} sub={`Hoy ${int(u.active_today)} · 7 días ${int(u.active_7)} · este mes ${int(u.active_month)}`}
                    tip="Usuarios con sesión que han usado la app (cualquier petición con sesión). Se cuenta desde el 4-nov-2026." />
                <Stat testid="sc-per-active" label="Coste por usuario activo" value={u.active_30 ? usd((t.last_30 || 0) / u.active_30) : "—"} sub="Total de 30 días ÷ activos en 30 días" />
                <Stat testid="sc-per-registered" label="Coste por usuario registrado" value={u.registered ? usd((t.last_30 || 0) / u.registered) : "—"} sub="Total de 30 días ÷ registrados" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                <ChartCard testid="sc-chart-monthly" title="Coste por mes" icon={BarChart3} data={monthly} groups={groups} users filename="coste-servicios-mensual"
                    tip={"Coste de cada mes de los últimos 12 por tipo de servicio (el mes en curso, hasta hoy), con los usuarios activos de cada mes en el eje derecho para ver cómo cambia el coste con su número."}
                    note="Barras: coste del mes por tipo de servicio (eje izquierdo, $). Línea: usuarios activos del mes (eje derecho), desde el 4-nov-2026. El último mes va hasta hoy." />
                <ChartCard testid="sc-chart-daily" title={`Coste por día · ${days} días`} icon={CalendarDays} data={daily} groups={groups} filename="coste-servicios-diario"
                    tip={"Coste de cada día por tipo de servicio. Los fijos aparecen repartidos a partes iguales entre los días del mes."}
                    note="Coste diario por tipo de servicio ($). Los servicios fijos, repartidos entre los días del mes." />
            </div>
            <div className="flex items-center gap-2 mb-4 flex-wrap text-xs">
                <span className="text-[#4A4A4A]">Días del gráfico diario:</span>
                {[30, 90, 180, 365].map((n) => (
                    <button key={n} onClick={() => setDays(n)} className={`px-2 py-0.5 border ${days === n ? "bg-black text-[#FDF1E6] border-black" : "border-black/30 hover:border-black"}`}>{n}</button>
                ))}
            </div>

            <div className="mb-4"><ServicesTable rows={data.rows || []} totals={t} groups={groups} /></div>
            {unpriced.length > 0 && (
                <p className="text-[11px] text-[#7a5a10] mb-4">Sin precio configurado (cuentan 0 $): {unpriced.map(([m, n]) => `${m} (${int(n)} llamadas)`).join(", ")}. Añádelo en <code>AI_PRICES_JSON</code>.</p>
            )}

            <div className="mb-4"><UsersTable per={per} users={u} /></div>
            <div className="mb-4"><FixedServicesEditor settings={data.settings} onSaved={load} /></div>

            {marketstackPanel && <div className="mt-6 border-t border-black pt-5">{marketstackPanel}</div>}

            <ul className="text-[11px] text-[#7A7A7A] mt-4 space-y-1 list-disc pl-4">
                {(data.notes || []).map((n) => <li key={n}>{n}</li>)}
            </ul>
        </div>
    );
}
